import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { JsonEditor as Editor } from 'jsoneditor-react';
import 'jsoneditor-react/es/editor.min.css';

import ModalDiff from './ModalDiff';

import Spinner from 'react-bootstrap/Spinner';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';

import * as api from 'api/pricing';

const WHITELIST_KEYS = [
  'PRICE_MATRICES',
  'URGENCY_COEFFICIENTS',
  'SHIPPER_SEGMENT_COEFFICIENTS',
  'SHIPPER_REVENUE_COEFFICIENTS',
  'SUPPLY_LEVEL_COEFFICIENTS',
  'PRICE_MULTIPLIERS_CAP'
];

const filterAllowedFields = obj => {
  const whiteListedObj = Object.keys(obj || {})
    .filter(key => WHITELIST_KEYS.includes(key))
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: obj[key]
      }),
      {}
    );
  return whiteListedObj;
};

const PricingCreate = React.memo(({ match }) => {
  const editorRef = useRef();
  const [expertMode, setExpertMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentBase, setCurrentBase] = useState(null);
  const [newJSON, setNewJSON] = useState(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const history = useHistory();

  const {
    params: { strategy },
    url = ''
  } = match;

  const urlPath = url.split('/');
  const basePricingURL = `/${urlPath.slice(1, urlPath.length - 1).join('/')}`;

  const { config: currentConfig, tag: currentTag, domain_code: currentDomainCode } =
    currentBase || {};

  const currentConfigWhiteListed = filterAllowedFields(currentConfig);

  const editorMode = expertMode ? 'code' : 'form';
  const editorConfig = expertMode ? currentConfig : currentConfigWhiteListed;

  const oldValue = JSON.stringify(editorConfig, null, 2);
  const newValue = newJSON && JSON.stringify(newJSON, null, 2);

  const previewDisabled = !newValue || oldValue === newValue;
  const expertViewDisabled = !newValue || oldValue === newValue;

  const handleChange = useCallback(data => {
    setNewJSON(data);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowDiffModal(false);
  }, []);

  const handleDiffPreview = useCallback(() => {
    setShowDiffModal(true);
  }, []);

  const handleNewVersion = useCallback(
    async tag => {
      await api.postPricingVersion({
        domain_code: currentDomainCode,
        strategy: strategy,
        tag,
        config: expertMode ? newJSON : { ...currentConfig, ...newJSON }
      });
      history.replace(basePricingURL);
    },
    [expertMode, strategy, basePricingURL, history, currentConfig, currentDomainCode, newJSON]
  );

  const handleCheckbox = useCallback(() => {
    setExpertMode(value => !value);
  }, []);

  useEffect(() => {
    setLoading(true);

    async function getPricingVersions() {
      try {
        const currentBase = await api.getCurrentPricingVersionDetail(strategy);
        setCurrentBase(currentBase);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    getPricingVersions();
  }, [strategy]);

  useEffect(() => {
    if (!editorRef || !editorRef.current) {
      return;
    }

    const { current } = editorRef;

    const instance = current.jsonEditor;

    if (!instance) {
      return;
    }

    instance.setMode(editorMode);
    instance.set(editorConfig);
    //eslint-disable-next-line
  }, [expertMode]);

  if (loading) {
    return (
      <div className="mt-4 d-flex justify-content-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div>
        <h2 className="mb-3">Create new version for {strategy}</h2>
        <Form.Check
          id="expert-view-pricing"
          type="checkbox"
          label="Expert view"
          className="mb-4"
          value={expertMode}
          onChange={handleCheckbox}
          disabled={!expertViewDisabled}
        />

        <Editor
          value={editorConfig}
          onChange={handleChange}
          history={true}
          mode={editorMode}
          ref={editorRef}
        />

        <div className="d-flex justify-content-end mt-4 mb-4">
          <Link to={basePricingURL}>
            <Button className="mr-2" variant="outline-danger">
              Cancel
            </Button>
          </Link>
          <Button variant="primary" disabled={previewDisabled} onClick={handleDiffPreview}>
            Preview changes
          </Button>
        </div>
      </div>
      {!previewDisabled && (
        <ModalDiff
          onHide={handleModalClose}
          show={showDiffModal}
          oldValue={oldValue}
          newValue={newValue}
          currentTag={currentTag}
          onSave={handleNewVersion}
        />
      )}
    </>
  );
});

export default PricingCreate;
